/**
 * useResetScrollOnFocus — Hook qui scroll en haut quand on revient sur l'écran
 *
 * Usage :
 *   import { useResetScrollOnFocus } from '../hooks/useResetScrollOnFocus';
 *
 *   function MyScreen() {
 *     const scrollRef = useResetScrollOnFocus();
 *     return <ScrollView ref={scrollRef}>...</ScrollView>;
 *   }
 *
 * À chaque fois que l'écran reprend le focus (l'user navigue dessus depuis
 * un autre écran ou tab), le scroll position est remis à 0.
 *
 * Compatible avec :
 *   - ScrollView (par défaut)
 *   - FlatList   (passer { isList: true })
 *   - SectionList (idem)
 */
import { useRef, useCallback } from 'react';
import { useFocusEffect } from '@react-navigation/native';

/**
 * @param {Object} options
 *   - isList : true si le ref est attaché à FlatList/SectionList (utilise scrollToOffset)
 *   - animated : false par défaut (reset instantané, plus net)
 *   - skipFirstFocus : true par défaut (ne reset pas au tout 1er affichage,
 *     l'écran s'ouvre déjà en haut)
 */
export function useResetScrollOnFocus(options = {}) {
  const {
    isList = false,
    animated = false,
    skipFirstFocus = true,
  } = options;

  const scrollRef = useRef(null);
  const firstFocusRef = useRef(true);

  useFocusEffect(
    useCallback(() => {
      // Skip le tout 1er focus pour éviter un appel inutile au mount
      if (firstFocusRef.current && skipFirstFocus) {
        firstFocusRef.current = false;
        return;
      }
      // Reset après un micro-tick pour laisser le rendu se faire
      const timer = setTimeout(() => {
        if (!scrollRef.current) return;
        try {
          if (isList) {
            // FlatList / SectionList
            scrollRef.current.scrollToOffset?.({ offset: 0, animated });
          } else {
            // ScrollView
            scrollRef.current.scrollTo?.({ x: 0, y: 0, animated });
          }
        } catch (e) {
          // Silencieux — pas de raison de planter pour un scroll
        }
      }, 50);
      return () => clearTimeout(timer);
    }, [isList, animated, skipFirstFocus])
  );

  return scrollRef;
}

export default useResetScrollOnFocus;
